// ============================================
// IMPERSONATION — "view as" session handling
// ============================================
// A super admin can open the portal as a client/subcontractor/colleague so support can reproduce
// what they are seeing. The backend returns a REAL session for that user (see
// /api/auth/impersonate), so the only job here is to keep the two identities apart: your own
// session is parked under `impersonate:from` and put back exactly as it was on exit.
//
// While viewing as someone, these keys all belong to the TARGET, exactly as if they had logged in:
//   token · supabase_token · supabase_refresh_token · user · subbieId
//
// Everything else in the app already reads those keys, so no page needed changing for the view to
// be truthful — including RLS on the backend, which is scoped by the target's own Supabase token.

const SESSION_KEYS = ['token', 'supabase_token', 'supabase_refresh_token', 'user', 'subbieId'];
const FROM_KEY = 'impersonate:from';
const ACTIVE_KEY = 'impersonate:active';

export const isImpersonating = () => !!localStorage.getItem(ACTIVE_KEY);

export const impersonationInfo = () => {
  if (!isImpersonating()) return null;
  try {
    return JSON.parse(localStorage.getItem(ACTIVE_KEY) || 'null');
  } catch {
    return null;
  }
};

// where the super admin lands once they exit
export const ownHome = () => '/dashboard';

// where the person being viewed lives
export const homeForRole = (role) => (
  role === 'client' ? '/client-dashboard'
    : (role === 'subcontractor' || role === 'subbie') ? '/subbie-dashboard'
      : '/dashboard'
);

export const beginImpersonation = (data) => {
  // Park the real session FIRST — this is the only copy, and Exit depends entirely on it.
  const saved = {};
  SESSION_KEYS.forEach((key) => {
    const value = localStorage.getItem(key);
    if (value !== null) saved[key] = value;
  });
  localStorage.setItem(FROM_KEY, JSON.stringify(saved));

  localStorage.setItem('token', data.token);
  if (data.supabase_token) localStorage.setItem('supabase_token', data.supabase_token);
  else localStorage.removeItem('supabase_token');
  if (data.supabase_refresh_token) localStorage.setItem('supabase_refresh_token', data.supabase_refresh_token);
  else localStorage.removeItem('supabase_refresh_token');
  localStorage.setItem('user', JSON.stringify(data.user));
  // `subbieId` is a cached company id the subbie screens trust — it must never carry over from the
  // previous identity or a super admin would see their own company's fleet.
  if (data.user.role === 'subcontractor' && data.role_data && data.role_data.id) {
    localStorage.setItem('subbieId', data.role_data.id);
  } else {
    localStorage.removeItem('subbieId');
  }

  localStorage.setItem(ACTIVE_KEY, JSON.stringify({
    by: (data.impersonation && data.impersonation.by) || null,
    as: (data.impersonation && data.impersonation.as) || {
      id: data.user.id, email: data.user.email, full_name: data.user.full_name, role: data.user.role
    },
    started_at: new Date().toISOString(),
    expires_in: (data.impersonation && data.impersonation.expires_in) || 3600
  }));
};

export const endImpersonation = () => {
  let saved = null;
  try {
    saved = JSON.parse(localStorage.getItem(FROM_KEY) || 'null');
  } catch {
    saved = null;
  }
  if (saved) {
    SESSION_KEYS.forEach((key) => {
      if (saved[key] !== undefined) localStorage.setItem(key, saved[key]);
      else localStorage.removeItem(key);
    });
  }
  localStorage.removeItem(FROM_KEY);
  localStorage.removeItem(ACTIVE_KEY);
  return saved;
};

// A super admin viewing as someone is never themselves a super admin, so the buttons below
// disappear of their own accord mid-impersonation — no nested "view as" is possible.
export const canImpersonate = () => {
  try {
    const me = JSON.parse(localStorage.getItem('user') || '{}');
    return me.role === 'super_admin' && !isImpersonating();
  } catch {
    return false;
  }
};
